'use client';

import { useEffect, useState } from 'react';
import { DialogContent, DialogOverlay } from '@relm/ui/Dialog';

import { SupportForm } from '@/components/Header/Support/SupportForm';
import { SupportSuccess } from '@/components/Header/Support/SupportSuccess';

interface SupportPrefill {
  topic?: string;
  question?: string;
}

/**
 * Listener-only mount of the support modal for the public help center.
 *
 * The visible Support trigger lives in the dashboard `Header` (signed-in
 * users only). On the public `/help` tree there's no equivalent host, so
 * `StillNeedHelp` and `HelpFeedback` were dispatching `open-support`
 * events into the void. Mounting this provider on the help layout wires
 * those events back to a real dialog, while keeping the modal lazy
 * (it only renders chrome when `isOpen`).
 *
 * Renders nothing visible until an event arrives.
 */
export function HelpSupportProvider() {
  const [isOpen, setIsOpen] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [prefill, setPrefill] = useState<SupportPrefill>({});

  useEffect(() => {
    function handleOpenSupport(e: Event) {
      const detail = (e as CustomEvent<SupportPrefill>).detail ?? {};
      setPrefill(detail);
      setIsOpen(true);
      setIsSuccess(false);
    }
    window.addEventListener('open-support', handleOpenSupport);
    return () => window.removeEventListener('open-support', handleOpenSupport);
  }, []);

  const handleSuccess = () => {
    setIsSuccess(true);
    setTimeout(() => {
      setIsOpen(false);
      setIsSuccess(false);
      setPrefill({});
    }, 3000);
  };

  const handleClose = () => {
    setIsOpen(false);
    setIsSuccess(false);
    setPrefill({});
  };

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <DialogContent className="sm:max-w-lg">
        {isSuccess ?
          <SupportSuccess onClose={handleClose} />
        : <SupportForm
            onSuccess={handleSuccess}
            onCancel={handleClose}
            defaultTopic={prefill.topic}
            defaultQuestion={prefill.question}
          />
        }
      </DialogContent>
    </DialogOverlay>
  );
}
